/**
 * Stats Row Block
 *
 * Counts each figure up from zero the first time the block is scrolled into
 * view. The final values are already in the markup, so this only ever takes
 * them away and puts them back — with no JavaScript, or no IntersectionObserver,
 * the correct numbers are on screen from the start.
 */

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const STAGGER = 120;

function prefersReducedMotion() {
    return typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION).matches;
}

// Rebuild the figure the way the editor typed it, so a value entered as
// "1,250.5" counts through "1,024.8" rather than "1024.8".
function formatValue(value, decimals, separator) {
    if (typeof Intl === 'undefined' || typeof Intl.NumberFormat !== 'function') {
        return separator
            ? value.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            : value.toFixed(decimals);
    }

    return new Intl.NumberFormat(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: separator,
    }).format(value);
}

function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
}

function countUp(element, duration, delay) {
    const target = parseFloat(element.dataset.statTarget);

    if (Number.isNaN(target)) return;

    const decimals = parseInt(element.dataset.statDecimals, 10) || 0;
    const separator = element.dataset.statSeparator === 'true';
    const finalText = element.textContent;

    element.textContent = formatValue(0, decimals, separator);

    let start = null;

    const step = (timestamp) => {
        if (start === null) start = timestamp;

        const elapsed = timestamp - start - delay;

        if (elapsed < 0) {
            window.requestAnimationFrame(step);
            return;
        }

        const progress = Math.min(elapsed / duration, 1);

        if (progress < 1) {
            element.textContent = formatValue(easeOutCubic(progress) * target, decimals, separator);
            window.requestAnimationFrame(step);
            return;
        }

        // Land on the editor's own string, so any formatting this script does
        // not reproduce survives the animation.
        element.textContent = finalText;
    };

    window.requestAnimationFrame(step);
}

function runCounters(block) {
    const values = block.querySelectorAll('.stats-row__value[data-stat-target]');

    if (!values.length) return;

    const duration = parseInt(block.dataset.statsDuration, 10) || 2000;

    values.forEach((value, index) => countUp(value, duration, index * STAGGER));
}

function initStatsRow(scope = document) {
    const blocks = scope.querySelectorAll('.stats-row[data-stats-animate]');

    blocks.forEach((block) => {
        if (block.dataset.statsInitialised) return;
        block.dataset.statsInitialised = 'true';

        // Nothing to gain from animating for anyone who has asked for less
        // motion, or in a browser that cannot tell us when the block is seen.
        if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;

                    observer.unobserve(entry.target);
                    runCounters(entry.target);
                });
            },
            { threshold: 0.25, rootMargin: '0px 0px -10% 0px' },
        );

        observer.observe(block);
    });
}

document.addEventListener('DOMContentLoaded', () => initStatsRow());

// Blocks are re-rendered as they are edited, so run again on each preview
// render (dispatched by resources/js/editor-block-init.js).
document.addEventListener('blockEditorInit', (e) => {
    if (e.detail && e.detail.block) {
        initStatsRow(e.detail.block);
    }
});
